import type { Target } from 'src/models/TimerTarget';

import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';
import { Api, useApiFormSubmit } from 'src/api';
import { Button, Modal, ModalBody, ModalFooter, Text } from 'src/components';
import { Style } from 'src/constants';
import { useTimer } from 'src/contexts';
import { FieldSelect, FieldText, FieldTimeSpent, FormWrap } from 'src/form';
import { Model } from 'src/models';
import { formatAsDate } from 'src/util';

import TimeLogTargetPicker from './TimeLogTargetPicker';

type Entry = Model.WorkDone.Logged<Api.WorkDone.V_List>;

type Values = {
  minutes: number | null;
  date: string | null;
  category: string | null;
};

type Props = {
  /** Null when logging something new rather than correcting an entry. */
  existing: Entry | null;
  onClose: () => void;
  onSaved: () => void;
};

export default function TimeLogEntryModal({
  existing,
  onClose,
  onSaved,
}: Props) {
  const apiSummaries =
    Api.CompanyProjectSummary.useReadCollectionProjectSummaryAsAgency();
  const { logged } = useTimer();

  const existingTarget = React.useMemo(
    () => (existing ? Model.TimerTarget.fromWorkDone(existing) : null),
    [existing]
  );
  const [target, setTarget] = React.useState<Target | null>(existingTarget);

  const submit = useApiFormSubmit(
    existing ? 'PATCH' : 'POST',
    existing ? `/work-dones/${existing.id}` : '/work-dones',
    { onSuccess: onSaved }
  );

  const form = useForm<Values>({
    defaultValues: {
      minutes: existing?.minutes ?? null,
      date: formatAsDate(existing?.loggedAt ?? new Date()),
      category: existing?.categoryString || null,
    },
    onSubmit: (values, formObject) => {
      if (!target) {
        formObject.addSubmitError(
          'minutes',
          'Choose what this time was spent on'
        );
        return false;
      }

      const originalDate = existing ? formatAsDate(existing.loggedAt) : null;
      const takesCategory = Model.TimerTarget.getCategories(target).length > 0;

      return submit(
        {
          ...Model.TimerTarget.getWriteBody(target),
          minutes: values.minutes,
          categoryString: takesCategory ? values.category : null,
          // Only sent when the date actually moves, so correcting a duration
          // leaves the entry where it sits within its day.
          ...(values.date && values.date !== originalDate
            ? {
                loggedAt: Model.WorkDone.moveToDate(
                  existing?.loggedAt ?? null,
                  values.date
                ),
              }
            : {}),
        },
        formObject
      );
    },
  });

  const summaries = apiSummaries.data?.['hydra:member'];

  // What the timer already knows about sits alongside the full list, which is
  // the only way a ticket gets in: there are far too many to offer up front.
  const options = React.useMemo(() => {
    const recent = logged.flatMap((entry) => {
      const recentTarget = Model.TimerTarget.fromWorkDone(entry);
      return recentTarget ? [recentTarget] : [];
    });

    return dedupe([
      ...(existingTarget ? [existingTarget] : []),
      ...recent,
      ...(summaries ? Model.TimerTarget.fromProjectSummaries(summaries) : []),
    ]);
  }, [existingTarget, logged, summaries]);

  const categories = target ? Model.TimerTarget.getCategories(target) : [];

  return (
    <Modal
      title={existing ? 'Edit entry' : 'Log time'}
      onClose={onClose}
      fullScreenOnMobile
    >
      <FormWrap {...form}>
        <ModalBody>
          <TimeLogTargetPicker
            options={options}
            selected={target}
            onSelect={(picked) => {
              setTarget(picked);
              // A ticket takes no category, and a project only takes its own
              // two, so one carried over from the last pick may not fit.
              const allowed = Model.TimerTarget.getCategories(picked);
              const current = form.getField('category').value;
              if (!current || !allowed.includes(current)) {
                form.getField('category').handleValueChange(allowed[0] ?? null);
              }
            }}
          />

          {categories.length > 0 && (
            <FieldSelect
              field={form.getField('category')}
              choices={categories.map((category) => ({
                value: category,
                label: Model.WorkDoneCategory.format(category),
              }))}
            />
          )}

          <html.div style={[Style.row, styles.row]}>
            <html.div style={styles.half}>
              <FieldText
                field={form.getField('date')}
                label="Date"
                type="date"
              />
            </html.div>
            <html.div style={styles.half}>
              <FieldTimeSpent
                field={form.getField('minutes')}
                hint="Rounded up to the nearest 5 minutes."
                autoFocus={!!existing}
              />
            </html.div>
          </html.div>

          {!summaries && (
            <Text typeface="caption.medium" color="neutral40">
              Loading what you can log against&hellip;
            </Text>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            label="Cancel"
            variant="text"
            colorScheme="black"
            onClick={onClose}
          />
          <Button
            label={existing ? 'Save changes' : 'Log time'}
            onClick={form.handleSubmit}
            disabled={form.isLoading}
          />
        </ModalFooter>
      </FormWrap>
    </Modal>
  );
}

function dedupe(targets: ReadonlyArray<Target>): Array<Target> {
  const byIri = new Map<string, Target>();
  for (const target of targets) {
    if (!byIri.has(target.iri)) byIri.set(target.iri, target);
  }
  return [...byIri.values()];
}

const styles = css.create({
  row: {
    gap: 12,
  },
  half: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
});
